(() => {
  /* ============================================
   * CONFIGURATION & CONSTANTS
   * ============================================ */

  const CYLINDO_CONFIG = {
    DEBUG: false,
    PLACEHOLDER_HIDE_DELAY_MS: 10,
    SHOW_LOADING_SPINNER: false,
    PRELOAD: {
      ENABLED: false,
      FABRICS_PER_PHASE: 8,
      SIZE_RADIUS: 2,
      PHASE_DELAY_MS: 500,
      MAX_CONCURRENT_REQUESTS: 3,
      INITIAL_THUMBNAIL_DEFER_TIMEOUT_MS: 500,
    },
    MAIN_VIEWER_FALLBACK_TIMEOUT_MS: 1000,
    PRODUCT_TYPES: {
      SOFA: 'Sofa',
      LOVESEAT: 'Loveseat',
      OTTOMAN: 'Ottoman',
      CHAIR: 'Chair',
      BED: 'Bed',
      SOFA_BED: 'Sofa Bed',
      PILLOWS: 'Pillows',
      MODULAR_PIECE: 'Modular Piece',
      SOFA_WITH_CHAISE: 'Sofa with Chaise',
      SOFA_WITH_BUMPER: 'Sofa with Bumper',
      SECTIONAL_WITH_CHAISE: 'sectional with chaise',
    },
    PRODUCTS_USE_WIDTH: ['Sofa', 'Loveseat', 'Ottoman', 'Sofa Bed', 'Sofa with Chaise', 'Sofa with Bumper', 'Modular Piece'],
    FEATURE_KEYS: ['width_1', 'width_2', 'width_3', 'orientation'],
    FRAMES: {
      DEFAULT: ['1', '30', '25', '20'],
      SECTIONAL: ['1', '30', '25', '13'],
      REVERSED: ['1', '5', '9', '21'],
    },
    STATES: {
      OPEN: 'OPEN',
      CLOSE: 'CLOSE',
    },
  };

  /* ============================================
   * UTILITIES
   * ============================================ */

  /**
   * Creates a namespaced logger that respects DEBUG setting
   * @param {string} namespace - Logger namespace for identification
   * @param {boolean} enabled - Whether logging is enabled
   * @returns {Object} Logger object with log, warn, error methods
   */
  const createLogger = (namespace, enabled = CYLINDO_CONFIG.DEBUG) => ({
    log: (...args) => enabled && console.log(`[${namespace}]`, ...args),
    warn: (...args) => enabled && console.warn(`[${namespace}]`, ...args),
    error: (...args) => console.error(`[${namespace}]`, ...args),
  });

  const logger = createLogger('Cylindo');

  /**
   * Creates a debounced version of a function
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in milliseconds
   * @returns {Function} Debounced function
   */
  const debounce = (fn, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  /* ============================================
   * SHARED DOM CACHE
   * ============================================ */

   /**
   * Lazily cached DOM elements shared across all frame trigger instances
   * Prevents redundant document queries on each component instantiation  
   */  
  const sharedDOMCache = {
    _cylindoCoverMobile: null,
    _cylindoCoverDesktop: null,
    _cylindoOpenTriggers: null,
    _initialized: false,

    init() {
      if (this._initialized) return;
      this._cylindoCoverMobile = document.querySelector('[data-cylindo-cover-mobile]');
      this._cylindoCoverDesktop = document.querySelector('[data-cylindo-cover-desktop]');
      this._initialized = true;
      logger.log('SharedDOMCache initialized');
    },

    get cylindoCoverMobile() {
      if (!this._initialized) this.init();
      return this._cylindoCoverMobile;
    },

    get cylindoCoverDesktop() {
      if (!this._initialized) this.init();
      return this._cylindoCoverDesktop;
    },

    get cylindoOpenTriggers() {
      return document.querySelectorAll('td-cylindo-open-trigger');
    },

  };

  const viewportSelectionCache = {
    customizerDetail: null,
    isOpen: null,
  };

  const mergeCustomizerDetailIntoCache = (detail) => {
    if (!detail || typeof detail !== 'object') return;

    const normalizedDetail = { ...detail };
    if (!normalizedDetail.fabric && normalizedDetail.newValue?.sku_code) {
      normalizedDetail.fabric = normalizedDetail.newValue;
    }

    viewportSelectionCache.customizerDetail = viewportSelectionCache.customizerDetail
      ? { ...viewportSelectionCache.customizerDetail, ...normalizedDetail }
      : normalizedDetail;
  };

  const getCachedOpenState = () => {
    if (typeof viewportSelectionCache.isOpen === 'boolean') {
      return viewportSelectionCache.isOpen;
    }

    const openInput = document.querySelector('td-cylindo-open-trigger input');
    if (!openInput) return null;
    return Boolean(openInput.checked);
  };

  document.addEventListener('td:product-selected', ({ detail }) => {
    mergeCustomizerDetailIntoCache(detail);
  });

  document.addEventListener('td:color-changed', ({ detail }) => {
    mergeCustomizerDetailIntoCache(detail);
  });

  document.addEventListener('td:cylindo-open-trigger', ({ detail }) => {
    if (typeof detail?.open === 'boolean') {
      viewportSelectionCache.isOpen = detail.open;
    }
  });

  /**
   * Safely extracts a dimension value from an array of dimension objects
   * @param {Array} dimensions - Array of dimension objects
   * @param {string} key - The dimension key to find
   * @returns {*} The dimension value or null if not found
   */
  const getDimensionValue = (dimensions, key) => {
    if (!dimensions || !Array.isArray(dimensions)) return null;
    const found = dimensions.find((dim) => dim.hasOwnProperty(key));
    return found ? found[key] : null;
  };

  /**
   * Returns the correct frame array based on product type and orientation
   * @param {string} productType - The product type
   * @param {string} orientation - Optional orientation (for reversed frames)
   * @returns {Array} Array of frame numbers
   */
  const getFrames = (productType, orientation = null) => {
    if (orientation && orientation.includes('Right')) {
      return CYLINDO_CONFIG.FRAMES.REVERSED;
    }
    if (productType && productType.includes('Sectional')) {
      return CYLINDO_CONFIG.FRAMES.SECTIONAL;
    }
    return CYLINDO_CONFIG.FRAMES.DEFAULT;
  };

  /* ============================================
   * FEATURE BUILDER
   * ============================================ */

  /**
   * Builds Cylindo feature objects and URL fragments from customizer detail
   * Centralizes all product-type-specific logic in one place
   */
  class CylindoFeatureBuilder {
    constructor(productType) {
      this.productType = productType;
    }

    /**
     * Builds a features object for the Cylindo viewer
     * @param {Object} detail - The customizer event detail
     * @param {Object} options - Additional options (e.g., isOpen for VIEW feature)
     * @returns {Object} Features object { 'UPHOLSTERY': 'value', ... }
     */
    buildFeatures(detail, options = {}) {
      const features = {};
      const dimensions = detail.product?.dimensions;

      this._addWidthFeatures(features, dimensions, detail);
      this._addProductOverrides(features, dimensions);
      this._addMaterialFeatures(features, detail);

      if (options.includeView !== false) {
        const isOpenTriggerPresent = document.querySelector('td-cylindo-open-trigger[data-is-open="OPEN"]');
        if (isOpenTriggerPresent) {
          features['VIEW'] = CYLINDO_CONFIG.STATES.OPEN;
        }
      }

      return features;
    }

    /**
     * Builds URL-encoded feature fragments for the Cylindo Content API
     * @param {Object} detail - The customizer event detail
     * @param {Object} options - Additional options (e.g., isOpen for VIEW feature)
     * @returns {Array} Array of URL fragments ['UPHOLSTERY:value', ...]
     */
    buildUrlFragments(detail, options = {}) {
      const fragments = [];
      const dimensions = detail.product?.dimensions;

      this._addWidthFragments(fragments, dimensions, detail);
      this._addProductOverrideFragments(fragments, dimensions);
      this._addMaterialFragments(fragments, detail);

      if (options.includeView !== false && document.querySelector('td-cylindo-open-trigger')) {
        const isOpen = options.isOpen || false;
        fragments.push(`VIEW:${isOpen ? CYLINDO_CONFIG.STATES.OPEN : CYLINDO_CONFIG.STATES.CLOSE}`);
      }

      return fragments;
    }

    _addWidthFeatures(features, dimensions, detail) {
      const hasWidthDimension = ['width_1', 'width_2', 'width_3'].some((key) => getDimensionValue(dimensions, key));
      const shouldAddWidth = hasWidthDimension ||
        CYLINDO_CONFIG.PRODUCTS_USE_WIDTH.includes(this.productType);

      if (!shouldAddWidth) return;

      CYLINDO_CONFIG.FEATURE_KEYS.forEach((key) => {
        const value = getDimensionValue(dimensions, key);
        if (value) {
          const transformedKey = key.replace(/_/gm, ' ').toUpperCase();
          features[transformedKey] = value;
        } else if (key === 'orientation') {
          const orientation = detail.product?.orientation;
          if (orientation) {
            features['ORIENTATION'] = orientation.replace(/_/gm, ' ');
          }
        }
      });
    }

    _addProductOverrides(features, dimensions) {
      const productType = this.productType;
      const productTypeLower = productType?.toLowerCase();

      if (productType === CYLINDO_CONFIG.PRODUCT_TYPES.PILLOWS) {
        const pillowSize = getDimensionValue(dimensions, 'pillow_size');
        if (pillowSize) {
          features['PILLOW SIZE'] = pillowSize.replace(/"/g, '"');
        }
      }

      if (productType === CYLINDO_CONFIG.PRODUCT_TYPES.CHAIR) {
        const width1 = getDimensionValue(dimensions, 'width_1');
        if (width1) {
          features['WIDTH 1'] = width1;
        }
      }

      if (productTypeLower === CYLINDO_CONFIG.PRODUCT_TYPES.SECTIONAL_WITH_CHAISE) {
        delete features['WIDTH 2'];
      }

      if (productType === CYLINDO_CONFIG.PRODUCT_TYPES.OTTOMAN) {
        const depth = getDimensionValue(dimensions, 'depth');
        if (depth) {
          features['DEPTH SIZE'] = depth;
        }
      }

      const bedSize = getDimensionValue(dimensions, 'bed_size');
      if (bedSize) {
        features['OPTION1 VALUE'] = bedSize.toUpperCase();
      }

      const headboardHeight = getDimensionValue(dimensions, 'headboard_height');
      if (headboardHeight) {
        features['HEADBOARD HEIGHT'] = headboardHeight.split('(')[0].trim().toUpperCase();
      }
    }

    _addMaterialFeatures(features, detail) {
      if (detail.fabric?.sku) {
        features['UPHOLSTERY'] = detail.fabric.sku;
      }
      if (detail.leg?.sku) {
        features['LEG FINISH'] = detail.leg.sku;
      }
      if (detail.cushion?.style) {
        features['CUSHION'] = detail.cushion.style;
      }
    }

    _addWidthFragments(fragments, dimensions, detail) {
      const hasWidthDimension = ['width_1', 'width_2', 'width_3'].some((key) => getDimensionValue(dimensions, key));
      const shouldAddWidth = hasWidthDimension ||
        CYLINDO_CONFIG.PRODUCTS_USE_WIDTH.includes(this.productType);

      if (!shouldAddWidth) return;

      CYLINDO_CONFIG.FEATURE_KEYS.forEach((key) => {
        const value = getDimensionValue(dimensions, key);
        if (value) {
          const encodedKey = key.replace(/_/gm, '%20').toUpperCase();
          fragments.push(`${encodedKey}:${encodeURIComponent(value)}`.toUpperCase());
        } else if (key === 'orientation') {
          const orientation = detail.product?.orientation;
          if (orientation) {
            const encodedKey = key.replace(/_/gm, '%20').toUpperCase();
            fragments.push(`${encodedKey}:${encodeURIComponent(orientation.replace(/_/gm, ' '))}`.toUpperCase());
          }
        }
      });
    }

    _addProductOverrideFragments(fragments, dimensions) {
      const productType = this.productType;
      const productTypeLower = productType?.toLowerCase();

      if (productType === CYLINDO_CONFIG.PRODUCT_TYPES.CHAIR) {
        const width1 = getDimensionValue(dimensions, 'width_1');
        if (width1) {
          const existingIndex = fragments.findIndex(str => str.includes('WIDTH%201'));
          if (existingIndex === -1) {
            fragments.push(`WIDTH%201:${encodeURIComponent(width1)}`);
          }
        }
      }

      if (productTypeLower === CYLINDO_CONFIG.PRODUCT_TYPES.SECTIONAL_WITH_CHAISE) {
        const index = fragments.findIndex(str => str.includes('WIDTH%202'));
        if (index !== -1) {
          fragments.splice(index, 1);
        }
      }

      if (productType === CYLINDO_CONFIG.PRODUCT_TYPES.OTTOMAN) {
        const depth = getDimensionValue(dimensions, 'depth');
        if (depth) {
          fragments.push(`DEPTH%20SIZE:${encodeURIComponent(depth)}`);
        }
      }

      const bedSize = getDimensionValue(dimensions, 'bed_size');
      if (bedSize) {
        fragments.push(`OPTION1%20VALUE:${encodeURIComponent(bedSize.toUpperCase())}`);
      }

      const headboardHeight = getDimensionValue(dimensions, 'headboard_height');
      if (headboardHeight) {
        const formatted = headboardHeight.split('(')[0].trim().toUpperCase();
        fragments.push(`HEADBOARD%20HEIGHT:${encodeURIComponent(formatted)}`);
      }

      if (productType === CYLINDO_CONFIG.PRODUCT_TYPES.PILLOWS) {
        const pillowSize = getDimensionValue(dimensions, 'pillow_size');
        if (pillowSize) {
          fragments.push(`PILLOW%20SIZE:${encodeURIComponent(pillowSize.replace(/"/g, '"'))}`);
        }
      }
    }

    _addMaterialFragments(fragments, detail) {
      if (detail.fabric?.sku) {
        fragments.push(`UPHOLSTERY:${encodeURIComponent(detail.fabric.sku)}`);
      }
      if (detail.leg?.sku) {
        fragments.push(`LEG%20FINISH:${encodeURIComponent(detail.leg.sku)}`);
      }
      if (detail.cushion?.style) {
        fragments.push(`CUSHION:${encodeURIComponent(detail.cushion.style)}`);
      }
    }
  }

  /* ============================================
   * URL BUILDER
   * ============================================ */

  /**
   * Builds Cylindo Content API URLs from templates and features
   */
  class CylindoUrlBuilder {
    constructor(template, customerId, productCode) {
      this.template = template;
      this.customerId = customerId;
      this.productCode = productCode;
    }

    /**
     * Builds a complete URL for the Cylindo Content API
     * @param {string} frame - Frame number
     * @param {Array} featureFragments - Array of feature fragments
     * @returns {string} Complete URL
     */
    build(frame, featureFragments = []) {
      let url = this.template
        .replace('**customer**', this.customerId)
        .replace('**product**', this.productCode)
        .replace('**frame**', frame);

      url += '&ignoreunknownfeatures=true';

      if (featureFragments.length > 0) {
        const joinChar = url.includes('?') ? '&' : '?';
        url += `${joinChar}feature=${featureFragments.join('&feature=')}`;
      }

      return url;
    }

    /**
     * Updates an existing URL with a new feature value
     * @param {string} currentUrl - The current URL
     * @param {string} featureKey - The feature key (e.g., 'VIEW')
     * @param {string} newValue - The new value
     * @returns {string} Updated URL
     */
    updateFeature(currentUrl, featureKey, newValue) {
      const param = `feature=${featureKey}:${newValue}`;
      const oldPattern = new RegExp(`feature=${featureKey}:[A-Z]+`, 'i');

      if (oldPattern.test(currentUrl)) {
        return currentUrl.replace(oldPattern, param);
      }

      const joinChar = currentUrl.includes('?') ? '&' : '?';
      return `${currentUrl}${joinChar}${param}`;
    }
  }

  /* ============================================
   * PRELOAD MANAGER
   * ============================================ */

  /**
   * Manages preloading of Cylindo images on hover
   * Listens for td:cylindo-preload events from Vue components
   */
  class CylindoPreloadManager {
    constructor() {
      this.preloadedKeys = new Set();
      this.preloadedUrls = new Set();
      this.prefetchQueue = [];
      this.activePrefetchCount = 0;
      this.pendingPhases = [];
      this.phaseTimer = null;
      this.isMainUpdateInFlight = false;
      this.hasBootstrappedPhases = false;

      this.isInitialized = false;
      this.urlTemplate = null;
      this.customerId = null;
      this.productCode = null;
      this.featureBuilder = new CylindoFeatureBuilder(window.spsProductType);

      this._boundHandlePreload = this.handlePreload.bind(this);
      this._boundHandleCustomizerEvent = this.handleCustomizerEvent.bind(this);
      this._boundHandleMainUpdateStart = this.handleMainUpdateStart.bind(this);
      this._boundHandleMainViewerUpdated = this.handleMainViewerUpdated.bind(this);
    }

    /**
     * Initialize the preload manager with URL building parameters
     * Called when frame trigger elements are available
     */
    init(urlTemplate, customerId, productCode) {
      if (this.isInitialized) return;

      this.urlTemplate = urlTemplate;
      this.customerId = customerId;
      this.productCode = productCode;
      this.isInitialized = true;

      document.addEventListener('td:cylindo-preload', this._boundHandlePreload);
      document.addEventListener('td:color-changed', this._boundHandleCustomizerEvent);
      document.addEventListener('td:product-selected', this._boundHandleCustomizerEvent);
      document.addEventListener('td:cylindo-main-update-start', this._boundHandleMainUpdateStart);
      document.addEventListener('td:cylindo-main-updated', this._boundHandleMainViewerUpdated);
      logger.log('PreloadManager initialized');
    }

    handleMainUpdateStart() {
      this.isMainUpdateInFlight = true;
    }

    handleMainViewerUpdated() {
      this.isMainUpdateInFlight = false;
      this.processPrefetchQueue();
    }

    handleCustomizerEvent({ detail }) {
      if (!CYLINDO_CONFIG.PRELOAD.ENABLED || !detail || this.hasBootstrappedPhases) return;

      const phasePlans = this.buildPhasePlans(detail);
      if (phasePlans.length === 0) return;

      this.pendingPhases = phasePlans;
      this.hasBootstrappedPhases = true;
      this.startNextPhase(true);
    }

    /**
     * Handle preload events from Vue components
     */
    handlePreload({ detail }) {
      if (!this.isInitialized) {
        logger.warn('PreloadManager not initialized, skipping preload');
        return;
      }
      if (!detail) return;

      const fabricSku = this.extractFabricSku(detail.fabricSku || detail.fabric);
      if (!fabricSku) return;

      const normalizedDetail = {
        product: this.normalizeProduct(detail.product),
        fabric: { sku: fabricSku },
        leg: this.normalizeLeg(detail.leg),
        cushion: this.normalizeCushion(detail.cushion),
      };

      const cacheKey = this.buildCacheKey({
        fabricSku,
        product: normalizedDetail.product,
        leg: normalizedDetail.leg,
        cushion: normalizedDetail.cushion,
      });

      if (this.preloadedKeys.has(cacheKey)) {
        logger.log(`Skipping preload for ${cacheKey} - already cached`);
        return;
      }

      this.preloadedKeys.add(cacheKey);
      this.preloadThumbnails({
        ...normalizedDetail,
        fabricSku,
      }, { priority: 'interactive' });
    }

    /**
     * Build a unique cache key from all relevant state
     */
    buildCacheKey(detail) {
      const { fabricSku, product, leg, cushion } = detail;
      const dims = product?.dimensions?.map((d) => `${Object.keys(d)[0]}:${Object.values(d)[0]}`).join('-') || '';
      return `${fabricSku}-${leg?.sku || ''}-${cushion?.style || ''}-${product?.orientation || ''}-${dims}`;
    }

    /**
     * Preload thumbnail images for all frames with full feature set
     */
    preloadThumbnails(detail, { priority = 'background' } = {}) {
      const { fabricSku, product, leg, cushion } = detail;

      const preloadDetail = {
        product: this.normalizeProduct(product),
        fabric: { sku: fabricSku },
        leg: this.normalizeLeg(leg),
        cushion: this.normalizeCushion(cushion),
      };

      const fragments = this.featureBuilder.buildUrlFragments(preloadDetail, { includeView: false });
      const frames = this.getOrderedFrames(preloadDetail.product?.orientation);

      frames.forEach((frame) => {
        const url = this.buildPreloadUrl(frame, fragments);
        this.enqueuePrefetch(url, priority);
      });

      logger.log(`Preloading ${frames.length} frames for fabric: ${fabricSku}`);
    }

    getOrderedFrames(orientation) {
      const frames = getFrames(window.spsProductType, orientation);
      if (!Array.isArray(frames) || frames.length <= 1) return frames;

      const [primaryFrame, ...secondaryFrames] = frames;
      return [primaryFrame, ...secondaryFrames];
    }

    /**
     * Build a preload URL for a specific frame with all feature fragments
     */
    buildPreloadUrl(frame, fragments) {
      let url = this.urlTemplate
        .replace('**customer**', this.customerId)
        .replace('**product**', this.productCode)
        .replace('**frame**', frame);

      url += '&ignoreunknownfeatures=true';

      if (fragments.length > 0) {
        url += `&feature=${fragments.join('&feature=')}`;
      }

      return url;
    }

    /**
     * Preload image by creating an Image object
     * Forces immediate fetch and browser caching
     */
    enqueuePrefetch(url, priority = 'background') {
      if (!url || this.preloadedUrls.has(url)) return;

      this.preloadedUrls.add(url);
      if (priority === 'interactive') {
        this.prefetchQueue.unshift({ url, priority });
      } else {
        this.prefetchQueue.push({ url, priority });
      }

      this.processPrefetchQueue();
    }

    processPrefetchQueue() {
      if (!this.isInitialized || this.isMainUpdateInFlight) return;

      while (
        this.activePrefetchCount < CYLINDO_CONFIG.PRELOAD.MAX_CONCURRENT_REQUESTS &&
        this.prefetchQueue.length > 0
      ) {
        const next = this.prefetchQueue.shift();
        this.prefetchImage(next.url, next.priority);
      }

      if (this.activePrefetchCount === 0 && this.prefetchQueue.length === 0) {
        this.startNextPhase();
      }
    }

    prefetchImage(url, priority = 'background') {
      this.activePrefetchCount += 1;

      const img = new Image();
      img.decoding = 'async';

      if ('fetchPriority' in img) {
        img.fetchPriority = priority === 'interactive' ? 'high' : 'low';
      }

      const finalize = () => {
        img.onload = null;
        img.onerror = null;
        this.activePrefetchCount = Math.max(0, this.activePrefetchCount - 1);
        this.processPrefetchQueue();
      };

      img.onload = finalize;
      img.onerror = finalize;
      img.src = url;
    }

    buildPhasePlans(detail) {
      const normalizedProduct = this.normalizeProduct(detail.product);
      if (!normalizedProduct) return [];

      const currentFabricSku = this.extractFabricSku(detail.fabric);
      const fabricChunks = this.getFabricChunks(currentFabricSku);
      if (fabricChunks.length === 0) return [];

      const nearbyProducts = this.getNearbyProducts(normalizedProduct);
      const legs = this.getLegOptions(detail.leg);
      const cushions = this.getCushionOptions(detail.cushion);

      return fabricChunks.map((fabricSkus, index) => ({
        fabricSkus,
        products: nearbyProducts,
        legs,
        cushions,
        label: `phase-${index + 1}`,
      }));
    }

    startNextPhase(immediate = false) {
      if (this.pendingPhases.length === 0 || this.phaseTimer) return;

      const runPhase = () => {
        this.phaseTimer = null;
        const nextPhase = this.pendingPhases.shift();
        if (!nextPhase) return;
        this.runPhase(nextPhase);
      };

      if (immediate) {
        runPhase();
        return;
      }

      this.phaseTimer = window.setTimeout(runPhase, CYLINDO_CONFIG.PRELOAD.PHASE_DELAY_MS);
    }

    runPhase(phase) {
      let queuedUrls = 0;

      phase.fabricSkus.forEach((fabricSku) => {
        phase.products.forEach((product) => {
          phase.legs.forEach((leg) => {
            phase.cushions.forEach((cushion) => {
              const fragments = this.featureBuilder.buildUrlFragments(
                {
                  product,
                  fabric: { sku: fabricSku },
                  leg,
                  cushion,
                },
                { includeView: false }
              );

              const frames = this.getOrderedFrames(product?.orientation);
              frames.forEach((frame) => {
                const url = this.buildPreloadUrl(frame, fragments);
                this.enqueuePrefetch(url, 'background');
                queuedUrls += 1;
              });
            });
          });
        });
      });

      logger.log(`Queued ${queuedUrls} preload URLs for ${phase.label}`);
    }

    getFabricChunks(currentFabricSku) {
      const allFabrics = Array.isArray(window.spsFabrics) ? window.spsFabrics : [];
      const allSkus = allFabrics
        .map((fabric) => this.extractFabricSku(fabric))
        .filter(Boolean);

      const orderedSkus = [];
      const seen = new Set();

      const addSku = (sku) => {
        if (!sku || seen.has(sku)) return;
        seen.add(sku);
        orderedSkus.push(sku);
      };

      addSku(currentFabricSku);
      allSkus.forEach(addSku);

      const chunks = [];
      for (let i = 0; i < orderedSkus.length; i += CYLINDO_CONFIG.PRELOAD.FABRICS_PER_PHASE) {
        chunks.push(orderedSkus.slice(i, i + CYLINDO_CONFIG.PRELOAD.FABRICS_PER_PHASE));
      }

      return chunks;
    }

    getNearbyProducts(currentProduct) {
      const products = Array.isArray(window.spsProducts) ? window.spsProducts : [];
      const normalizedProducts = products
        .map((product) => this.normalizeProduct(product, currentProduct))
        .filter(Boolean);

      const bySignature = new Map();
      const upsertProduct = (product) => {
        const signature = this.getProductSignature(product);
        if (!signature || bySignature.has(signature)) return;
        bySignature.set(signature, product);
      };

      upsertProduct(currentProduct);
      normalizedProducts.forEach(upsertProduct);

      const dedupedProducts = Array.from(bySignature.values());
      if (dedupedProducts.length === 0) return [currentProduct];

      const currentSignature = this.getProductSignature(currentProduct);
      const currentIndex = dedupedProducts.findIndex((product) => this.getProductSignature(product) === currentSignature);
      const centeredIndex = currentIndex >= 0 ? currentIndex : 0;
      const start = Math.max(0, centeredIndex - CYLINDO_CONFIG.PRELOAD.SIZE_RADIUS);
      const end = Math.min(dedupedProducts.length - 1, centeredIndex + CYLINDO_CONFIG.PRELOAD.SIZE_RADIUS);

      return dedupedProducts.slice(start, end + 1);
    }

    getLegOptions(currentLeg) {
      const allLegs = Array.isArray(window.spsLegs) ? window.spsLegs : [];
      const orderedLegs = [];
      const seen = new Set();

      const addLeg = (leg) => {
        const normalized = this.normalizeLeg(leg);
        const sku = normalized?.sku;
        if (!sku || seen.has(sku)) return;
        seen.add(sku);
        orderedLegs.push(normalized);
      };

      addLeg(currentLeg);
      allLegs.forEach(addLeg);

      return orderedLegs.length > 0 ? orderedLegs : [null];
    }

    getCushionOptions(currentCushion) {
      const allCushions = Array.isArray(window.spsCushions) ? window.spsCushions : [];
      const orderedCushions = [];
      const seen = new Set();

      const addCushion = (cushion) => {
        const normalized = this.normalizeCushion(cushion);
        const style = normalized?.style;
        if (!style || seen.has(style)) return;
        seen.add(style);
        orderedCushions.push(normalized);
      };

      addCushion(currentCushion);
      allCushions.forEach(addCushion);

      return orderedCushions.length > 0 ? orderedCushions : [null];
    }

    normalizeProduct(product, fallbackProduct = null) {
      if (!product && !fallbackProduct) return null;
      if (product && Array.isArray(product.dimensions)) return product;

      const source = product || fallbackProduct;
      if (!source) return null;

      const dimensionKeys = [
        'width_1',
        'width_2',
        'width_3',
        'depth',
        'depth_size',
        'bed_size',
        'pillow_size',
        'headboard_height',
        'chaise_width',
        'chaise_length',
      ];

      const dimensions = dimensionKeys
        .map((key) => {
          const value = source[key];
          if (value == null || value === '') return null;
          return { [key]: value };
        })
        .filter(Boolean);

      if (dimensions.length === 0 && Array.isArray(fallbackProduct?.dimensions)) {
        return fallbackProduct;
      }

      return {
        ...source,
        dimensions,
        orientation: source.orientation || fallbackProduct?.orientation || null,
      };
    }

    normalizeLeg(leg) {
      const sku = this.extractLegSku(leg);
      return sku ? { sku } : null;
    }

    normalizeCushion(cushion) {
      const style = this.extractCushionStyle(cushion);
      return style ? { style } : null;
    }

    extractFabricSku(fabric) {
      if (!fabric) return null;
      if (typeof fabric === 'string') return fabric;
      return fabric.sku || fabric.sku_code || fabric.skuCode || null;
    }

    extractLegSku(leg) {
      if (!leg) return null;
      if (typeof leg === 'string') return leg;
      return leg.sku || leg.sku_code || leg.skuCode || null;
    }

    extractCushionStyle(cushion) {
      if (!cushion) return null;
      if (typeof cushion === 'string') return cushion;
      return cushion.style || cushion.cushion_style || cushion.option_value || cushion.sku || cushion.sku_code || cushion.name || null;
    }

    getProductSignature(product) {
      if (!product) return '';

      const dimensions = Array.isArray(product.dimensions)
        ? product.dimensions.map((dim) => `${Object.keys(dim)[0]}:${Object.values(dim)[0]}`).join('|')
        : '';

      return `${product.orientation || ''}|${dimensions}`;
    }

    /**
     * Clear preloaded keys cache (call on product change if needed)
     */
    clearCache() {
      this.preloadedKeys.clear();
      this.preloadedUrls.clear();
      this.prefetchQueue = [];
      this.pendingPhases = [];
      this.hasBootstrappedPhases = false;
      this.isMainUpdateInFlight = false;
      if (this.phaseTimer) {
        clearTimeout(this.phaseTimer);
        this.phaseTimer = null;
      }
      logger.log('PreloadManager cache cleared');
    }

    /**
     * Cleanup preload cache
     */
    cleanup() {
      this.preloadedKeys.clear();
      this.preloadedUrls.clear();
      this.prefetchQueue = [];
      this.pendingPhases = [];
      this.hasBootstrappedPhases = false;
      this.isMainUpdateInFlight = false;
      if (this.phaseTimer) {
        clearTimeout(this.phaseTimer);
        this.phaseTimer = null;
      }

      document.removeEventListener('td:cylindo-preload', this._boundHandlePreload);
      document.removeEventListener('td:color-changed', this._boundHandleCustomizerEvent);
      document.removeEventListener('td:product-selected', this._boundHandleCustomizerEvent);
      document.removeEventListener('td:cylindo-main-update-start', this._boundHandleMainUpdateStart);
      document.removeEventListener('td:cylindo-main-updated', this._boundHandleMainViewerUpdated);
    }
  }

  const preloadManager = new CylindoPreloadManager();

  /* ============================================
   * THUMBNAIL UPDATE COORDINATOR
   * ============================================ */

  /**
   * Coordinates thumbnail updates across all frame triggers
   * Ensures updates are debounced and race conditions are handled
   */
  class ThumbnailUpdateCoordinator {
    constructor() {
      this.pendingUpdate = null;
      this.updateId = 0;
      this.registeredTriggers = new Set();

      this.debouncedDispatch = debounce((detail) => {
        this._dispatchUpdate(detail);
      }, 100);
    }

    /**
     * Register a frame trigger instance
     */
    register(trigger) {
      this.registeredTriggers.add(trigger);
    }

    /**
     * Unregister a frame trigger instance
     */
    unregister(trigger) {
      this.registeredTriggers.delete(trigger);
    }

    /**
     * Queue an update - debounces rapid changes
     * @param {Object} detail - The customizer event detail
     */
    queueUpdate(detail) {
      this.debouncedDispatch(detail);
    }

    /**
     * Dispatch update to all registered triggers with a unique update ID
     * Triggers can use this ID to ignore stale responses
     */
    _dispatchUpdate(detail) {
      this.updateId++;
      const currentUpdateId = this.updateId;

      logger.log(`Dispatching coordinated update #${currentUpdateId} to ${this.registeredTriggers.size} triggers`);

      this.registeredTriggers.forEach((trigger) => {
        trigger.processUpdate(detail, currentUpdateId);
      });
    }

    /**
     * Get current update ID for staleness checks
     */
    getCurrentUpdateId() {
      return this.updateId;
    }
  }

  const thumbnailCoordinator = new ThumbnailUpdateCoordinator();

  /* ============================================
   * WEB COMPONENT: td-cylindo-wrapper
   * ============================================ */

  if (!customElements.get('td-cylindo-wrapper')) {
    class TDCylindoWrapper extends HTMLElement {
      constructor() {
        super();

        this.cylindoCustomerId = this.dataset.cylindoCustomer;
        this.cylindoProduct = this.dataset.cylindoProduct;
        this.urlTemplate = this.dataset.urlTemplate;
        this.placeholderUrlTemplate = this.dataset.placeholderUrlTemplate || this.urlTemplate;
        this.placeholderUrlTemplateDesktop =
          this.dataset.placeholderUrlTemplateDesktop || this.placeholderUrlTemplate;
        this.placeholderUrlTemplateMobile =
          this.dataset.placeholderUrlTemplateMobile || this.placeholderUrlTemplate;
        this.fixedFrame = this.dataset.fixedFrame ? String(this.dataset.fixedFrame) : null;
        this.isFixedFrame = Boolean(this.fixedFrame);
        this.cylindoWrapper = this.querySelector('[data-cylindo-wrapper]');
        this.loader = this.querySelector('[data-loader]');
        this.placeholder = this.querySelector('[data-cylindo-placeholder-dynamic]');

        this.cylindoViewer = undefined;
        this.isLoading = false;
        this.featureBuilder = new CylindoFeatureBuilder(window.spsProductType);
        this.urlBuilder = this.urlTemplate
          ? new CylindoUrlBuilder(this.urlTemplate, this.cylindoCustomerId, this.cylindoProduct)
          : null;
        this.placeholderUrlBuilder = this.placeholderUrlTemplate
          ? new CylindoUrlBuilder(this.placeholderUrlTemplate, this.cylindoCustomerId, this.cylindoProduct)
          : this.urlBuilder;
        this.placeholderUrlBuilderDesktop = this.placeholderUrlTemplateDesktop
          ? new CylindoUrlBuilder(this.placeholderUrlTemplateDesktop, this.cylindoCustomerId, this.cylindoProduct)
          : this.placeholderUrlBuilder;
        this.placeholderUrlBuilderMobile = this.placeholderUrlTemplateMobile
          ? new CylindoUrlBuilder(this.placeholderUrlTemplateMobile, this.cylindoCustomerId, this.cylindoProduct)
          : this.placeholderUrlBuilder;

        this._boundHandleCustomizerChange = this.handleCustomizerChange.bind(this);
        this._boundHandleFrameChange = this.handleFrameChange.bind(this);
        this._boundHandleOpenTrigger = this.handleOpenTrigger.bind(this);
        this._boundHandleCylindoLoaded = this.handleCylindoLoaded.bind(this);
        this._boundHandleCylindoInteractionReady = this.handleCylindoInteractionReady.bind(this);
        this._boundHandleCylindoImageSet = this.handleCylindoImageSet.bind(this);
        this._boundHandlePlaceholderPreview = this.handlePlaceholderPreview.bind(this);
        this._boundHandlePlaceholderError = this.handlePlaceholderError.bind(this);
        this._boundHandlePlaceholderLoad = this.handlePlaceholderLoad.bind(this);
        this._boundHandleCylindoFrameChange = this.handleCylindoFrameChange.bind(this);
        this._boundHandleViewportChange = this.handleViewportChange.bind(this);
        this.viewportMediaQuery = null;
        this.isViewportActive = false;
        this.isViewportListenerBound = false;
        this.debouncedApplyCustomizerChange = debounce((detail) => {
          this.queueCustomizerChange(detail);
        }, 80);
        this.lastFeatureSignature = null;
        this.lastAppliedFeatureSignature = null;
        this.lastQueuedFeatureSignature = null;
        this.pendingCustomizerRequest = null;
        this.inFlightFeatureSignature = null;
        this.featureSignatureByPlaceholderUrl = new Map();
        this.missingPreviewSignatures = new Set();
        this.mainUpdateToken = 0;
        this.mainUpdateInFlightToken = null;
        this.mainUpdateInFlightDetail = null;
        this.pendingPlaceholderSrc = null;
        this.pendingPlaceholderRetrySrc = null;
        this.didTryPendingPlaceholderRetry = false;
        this.mainViewerFallbackTimeout = null;
        this.pendingPlaceholderShow = false;
        this.placeholderFailed = false;
        this.activeFrame = this.fixedFrame || '1';
      }

      connectedCallback() {
        if (this.cylindoWrapper && this.cylindoCustomerId && this.cylindoProduct) {
          this.setupViewportListener();
          this.handleViewportChange();
        }
      }

      disconnectedCallback() {
        this.teardownViewportListener();
        this.deactivateForViewport();
      }

      setupViewportListener() {
        if (!this.viewportMediaQuery) {
          this.viewportMediaQuery = window.matchMedia('(min-width: 1024px)');
        }
        if (this.isViewportListenerBound) return;

        if (typeof this.viewportMediaQuery.addEventListener === 'function') {
          this.viewportMediaQuery.addEventListener('change', this._boundHandleViewportChange);
        } else if (typeof this.viewportMediaQuery.addListener === 'function') {
          this.viewportMediaQuery.addListener(this._boundHandleViewportChange);
        }

        this.isViewportListenerBound = true;
      }

      teardownViewportListener() {
        if (!this.viewportMediaQuery || !this.isViewportListenerBound) return;

        if (typeof this.viewportMediaQuery.removeEventListener === 'function') {
          this.viewportMediaQuery.removeEventListener('change', this._boundHandleViewportChange);
        } else if (typeof this.viewportMediaQuery.removeListener === 'function') {
          this.viewportMediaQuery.removeListener(this._boundHandleViewportChange);
        }

        this.isViewportListenerBound = false;
      }

      handleViewportChange() {
        const shouldBeActive = this.shouldBeActiveForViewport();

        if (shouldBeActive && !this.isViewportActive) {
          this.activateForViewport();
          return;
        }

        if (!shouldBeActive && this.isViewportActive) {
          this.deactivateForViewport();
        }
      }

      shouldBeActiveForViewport() {
        const isDesktop = this.viewportMediaQuery ? this.viewportMediaQuery.matches : window.matchMedia('(min-width: 1024px)').matches;
        const isDesktopOnly = Boolean(this.closest('.mobile--hide'));
        const isMobileOnly = Boolean(this.closest('.desktop--hide'));

        if (isDesktopOnly) return isDesktop;
        if (isMobileOnly) return !isDesktop;
        return true;
      }

      activateForViewport() {
        this.isViewportActive = true;
        this.initCylindoViewer();
        this.bindPlaceholderEvents();
        this.bindEvents();
      }

      deactivateForViewport() {
        this.isViewportActive = false;
        this.unbindEvents();
        this.unbindCylindoViewerEvents();
        this.unbindPlaceholderEvents();
        this.pendingPlaceholderSrc = null;
        this.pendingPlaceholderRetrySrc = null;
        this.didTryPendingPlaceholderRetry = false;
        this.mainUpdateInFlightToken = null;
        this.mainUpdateInFlightDetail = null;
        this.pendingCustomizerRequest = null;
        this.lastQueuedFeatureSignature = null;
        this.inFlightFeatureSignature = null;
        this.clearMainViewerFallbackTimeout();
      }

      unbindEvents() {
        document.removeEventListener('td:color-changed', this._boundHandleCustomizerChange);
        document.removeEventListener('td:product-selected', this._boundHandleCustomizerChange);
        document.removeEventListener('td:cylindo-frame-trigger', this._boundHandleFrameChange);
        document.removeEventListener('td:cylindo-open-trigger', this._boundHandleOpenTrigger);
        document.removeEventListener('td:cylindo-image-set', this._boundHandleCylindoImageSet);
        document.removeEventListener('td:cylindo-placeholder-preview', this._boundHandlePlaceholderPreview);
      }

      unbindCylindoViewerEvents() {
        if (!this.cylindoViewer) return;
        this.cylindoViewer.removeEventListener('loaded', this._boundHandleCylindoLoaded);
        this.cylindoViewer.removeEventListener('frame-change', this._boundHandleCylindoFrameChange);
        this.cylindoViewer.removeEventListener('interaction-ready', this._boundHandleCylindoInteractionReady);
      }

      unbindPlaceholderEvents() {
        if (!this.placeholder) return;
        this.placeholder.removeEventListener('error', this._boundHandlePlaceholderError);
        this.placeholder.removeEventListener('load', this._boundHandlePlaceholderLoad);
      }

      initCylindoViewer() {
        customElements.whenDefined('cylindo-viewer').then(() => {
          if (!this.isViewportActive) return;

          this.cylindoViewer = this.querySelector('cylindo-viewer');
          if (!this.cylindoViewer) {
            logger.error('cylindo-viewer element not found in template');
            return;
          }

          this.updateViewerTouchInteraction();
          this.bindCylindoViewerEvents();
          this.applyFixedFrame();
          this.hydrateFromCachedSelection();
          document.dispatchEvent(new CustomEvent('td:cylindo-loaded', {
            detail: {
              viewer: this.cylindoViewer,
              wrapper: this,
            },
          }));
        });
      }

      hydrateFromCachedSelection() {
        if (!this.isViewportActive || !this.cylindoViewer) return;

        const cachedOpen = getCachedOpenState();
        if (typeof cachedOpen === 'boolean') {
          this.cylindoViewer.features = {
            ...this.cylindoViewer.features,
            VIEW: cachedOpen ? CYLINDO_CONFIG.STATES.OPEN : CYLINDO_CONFIG.STATES.CLOSE,
          };
        }

        if (viewportSelectionCache.customizerDetail) {
          this.queueCustomizerChange(viewportSelectionCache.customizerDetail);
        }
      }

      /**
       * Bind events from the Cylindo viewer to manage loader state
       */
      bindCylindoViewerEvents() {
        if (!this.cylindoViewer) return;

        this.unbindCylindoViewerEvents();
        this.cylindoViewer.addEventListener('loaded', this._boundHandleCylindoLoaded);
        this.cylindoViewer.addEventListener('interaction-ready', this._boundHandleCylindoInteractionReady);
        this.cylindoViewer.addEventListener('frame-change', this._boundHandleCylindoFrameChange);
      }

      updateViewerTouchInteraction() {
        if (!this.cylindoViewer) return;

        const isDesktop = this.viewportMediaQuery
          ? this.viewportMediaQuery.matches
          : window.matchMedia('(min-width: 1024px)').matches;

        if (isDesktop) {
          this.cylindoViewer.style.pointerEvents = '';
          this.cylindoViewer.style.touchAction = '';
          return;
        }

        if (this.isFixedFrame) {
          // Keep viewer interactions enabled for zoom controls on fixed-frame slides.
          this.cylindoViewer.style.pointerEvents = '';
          this.cylindoViewer.style.touchAction = 'pinch-zoom pan-y';
          return;
        }

        // Default mobile behavior: avoid conflict with carousel gestures.
        this.cylindoViewer.style.pointerEvents = 'none';
        this.cylindoViewer.style.touchAction = 'pan-y';
      }

      applyFixedFrame() {
        if (!this.isFixedFrame || !this.cylindoViewer) return;
        this.activeFrame = this.fixedFrame;
      }

      bindEvents() {
        this.unbindEvents();
        document.addEventListener('td:color-changed', this._boundHandleCustomizerChange);
        document.addEventListener('td:product-selected', this._boundHandleCustomizerChange);
        document.addEventListener('td:cylindo-frame-trigger', this._boundHandleFrameChange);
        document.addEventListener('td:cylindo-open-trigger', this._boundHandleOpenTrigger);
        document.addEventListener('td:cylindo-image-set', this._boundHandleCylindoImageSet);
        document.addEventListener('td:cylindo-placeholder-preview', this._boundHandlePlaceholderPreview);
      }

      /**
       * Builds and sets placeholder URL from customizer detail (matches active frame from viewer)
       * Hides placeholder before setting src; load handler shows it only when image loads successfully
       */
      updatePlaceholderPreview(detail) {
        const activePlaceholderUrlBuilder = this.getActivePlaceholderUrlBuilder();
        if (!this.placeholder || !activePlaceholderUrlBuilder) return;

        this.hidePlaceholderBeforeLoad();
        this.pendingPlaceholderShow = true;

        const isOpen = document.querySelector('td-cylindo-open-trigger input')?.checked ?? false;
        const placeholderFrame = this.activeFrame || '1';
        const featureFragments = this.featureBuilder.buildUrlFragments(detail, { isOpen });
        const url = activePlaceholderUrlBuilder.build(placeholderFrame, featureFragments);
        const fallbackUrl = this.placeholderUrlBuilderMobile
          ? this.placeholderUrlBuilderMobile.build(placeholderFrame, featureFragments)
          : null;
        this.pendingPlaceholderSrc = url;
        this.pendingPlaceholderRetrySrc = fallbackUrl && fallbackUrl !== url ? fallbackUrl : null;
        this.didTryPendingPlaceholderRetry = false;
        if (this.lastFeatureSignature) {
          this.featureSignatureByPlaceholderUrl.set(url, this.lastFeatureSignature);
          if (this.pendingPlaceholderRetrySrc) {
            this.featureSignatureByPlaceholderUrl.set(this.pendingPlaceholderRetrySrc, this.lastFeatureSignature);
          }
        }
        this.placeholder.src = url;
      }

      getActivePlaceholderUrlBuilder() {
        const isDesktop = this.viewportMediaQuery
          ? this.viewportMediaQuery.matches
          : window.matchMedia('(min-width: 1024px)').matches;

        if (isDesktop) return this.placeholderUrlBuilderDesktop || this.placeholderUrlBuilder;
        return this.placeholderUrlBuilderMobile || this.placeholderUrlBuilder;
      }

      /**
       * Hides placeholder before loading new image (avoids broken-image flash)
       */
      hidePlaceholderBeforeLoad() {
        if (this.placeholder) {
          this.placeholder.classList.remove('td-cylindo-placeholder-dynamic--active');
          this.placeholder.classList.add('td-cylindo-placeholder-dynamic--hidden');
        }
      }

      /**
       * Binds load and error handlers - placeholder only shown when image loads successfully
       */
      bindPlaceholderEvents() {
        if (!this.placeholder) {
          this.placeholder = this.querySelector('[data-cylindo-placeholder-dynamic]');
        }
        if (this.placeholder) {
          this.unbindPlaceholderEvents();
          this.placeholder.addEventListener('load', this._boundHandlePlaceholderLoad);
          this.placeholder.addEventListener('error', this._boundHandlePlaceholderError);
        }
      }

      /**
       * Show placeholder when image loads successfully (avoids broken-image flash)
       */
      handlePlaceholderLoad() {
        if (this.pendingPlaceholderShow && this.placeholder) {
          const currentSrc = this.placeholder.currentSrc || this.placeholder.src;
          if (this.pendingPlaceholderSrc && currentSrc !== this.pendingPlaceholderSrc) return;
          this.placeholderFailed = false;
          this.pendingPlaceholderRetrySrc = null;
          this.didTryPendingPlaceholderRetry = false;
          const resolvedSignature = this.featureSignatureByPlaceholderUrl.get(currentSrc);
          if (resolvedSignature) {
            this.missingPreviewSignatures.delete(resolvedSignature);
          }
          this.placeholder.classList.remove('td-cylindo-placeholder-dynamic--hidden');
          this.placeholder.classList.add('td-cylindo-placeholder-dynamic--active');
          this.hideCover();
        }
      }

      /**
       * Handle Cylindo viewer 'frame-change' event
       * This is the source of truth for active frame - comes from the viewer
       */
      handleCylindoFrameChange({ detail }) {
        const frame = detail?.frame ?? detail;
        if (frame == null) return;

        if (this.isFixedFrame) {
          this.activeFrame = this.fixedFrame;
          return;
        }

        this.activeFrame = String(frame);
      }

      /**
       * Hide placeholder and loader when image fails to load (404, network error).
       * Show "this fabric has no preview" cover overlay.
       * Main viewer never shows default - only triggers use fallback.
       */
      handlePlaceholderError() {
        if (this.placeholder) {
          const currentSrc = this.placeholder.currentSrc || this.placeholder.src;
          if (this.pendingPlaceholderSrc && currentSrc !== this.pendingPlaceholderSrc) return;

          if (!this.didTryPendingPlaceholderRetry && this.pendingPlaceholderRetrySrc) {
            this.didTryPendingPlaceholderRetry = true;
            this.pendingPlaceholderSrc = this.pendingPlaceholderRetrySrc;
            this.placeholder.src = this.pendingPlaceholderRetrySrc;
            return;
          }

          const failedSignature = this.featureSignatureByPlaceholderUrl.get(currentSrc);
          if (failedSignature) {
            this.missingPreviewSignatures.add(failedSignature);
          }
        }
        this.showNoPreviewState();
      }

      showNoPreviewState() {
        this.pendingPlaceholderShow = false;
        this.placeholderFailed = true;
        if (this.placeholder) {
          this.placeholder.classList.remove('td-cylindo-placeholder-dynamic--active');
          this.placeholder.classList.add('td-cylindo-placeholder-dynamic--hidden');
        }
        this.hideLoader();

        document.dispatchEvent(new CustomEvent('td:cylindo-image-set', { detail: { image: null } }));

        const coverMobile = sharedDOMCache.cylindoCoverMobile;
        const coverDesktop = sharedDOMCache.cylindoCoverDesktop;
        if (coverMobile) {
          coverMobile.style.opacity = '1';
          coverMobile.style.zIndex = '10';
        }
        if (coverDesktop) {
          coverDesktop.style.opacity = '1';
          coverDesktop.style.zIndex = '10';
        }

        sharedDOMCache.cylindoOpenTriggers.forEach((trigger) => {
          trigger.classList.add('td--trigger-hidden');
        });
      }

      /**
       * Updates placeholder with hovered thumbnail URL (from frame trigger iteration 0)
       * Hides before setting src; load handler shows only when image loads successfully
       */
      handlePlaceholderPreview({ detail }) {
        if (this.placeholder && detail?.url) {
          this.hidePlaceholderBeforeLoad();
          this.pendingPlaceholderShow = true;
          this.pendingPlaceholderSrc = detail.url;
          this.pendingPlaceholderRetrySrc = null;
          this.didTryPendingPlaceholderRetry = false;
          this.placeholder.src = detail.url;
        }
      }

      /**
       * Handle customizer change event
       * Updates placeholder preview and shows loader
       */
      handleCustomizerChange({ detail }) {
        if (!this.isViewportActive || !this.cylindoViewer || !detail) return;
        mergeCustomizerDetailIntoCache(detail);
        this.debouncedApplyCustomizerChange(detail);
      }

      queueCustomizerChange(detail) {
        if (!this.isViewportActive || !this.cylindoViewer) return;

        const request = this.buildCustomizerRequest(detail);
        if (!request) return;

        const { featureSignature } = request;
        if (featureSignature === this.lastAppliedFeatureSignature) return;
        if (featureSignature === this.inFlightFeatureSignature) return;
        if (featureSignature === this.lastQueuedFeatureSignature) return;

        this.pendingCustomizerRequest = request;
        this.lastQueuedFeatureSignature = featureSignature;
        this.processPendingCustomizerRequest();
      }

      buildCustomizerRequest(detail) {
        const newFeatures = this.featureBuilder.buildFeatures(detail);
        const orderedFeatureKeys = Object.keys(newFeatures).sort();
        const featureSignature = JSON.stringify(orderedFeatureKeys.map((key) => [key, newFeatures[key]]));

        return {
          detail,
          newFeatures,
          featureSignature,
        };
      }

      processPendingCustomizerRequest() {
        if (!this.isViewportActive || !this.cylindoViewer) return;
        if (this.mainUpdateInFlightToken) return;
        if (!this.pendingCustomizerRequest) return;

        const request = this.pendingCustomizerRequest;
        this.pendingCustomizerRequest = null;
        this.lastQueuedFeatureSignature = null;
        this.applyCustomizerChange(request);
      }

      applyCustomizerChange({ detail, newFeatures, featureSignature }) {
        if (!this.isViewportActive || !this.cylindoViewer) return;

        this.lastFeatureSignature = featureSignature;
        this.inFlightFeatureSignature = featureSignature;
        this.mainUpdateToken += 1;
        this.mainUpdateInFlightToken = this.mainUpdateToken;
        this.mainUpdateInFlightDetail = detail;
        this.scheduleMainViewerFallbackTimeout(this.mainUpdateInFlightToken);
        document.dispatchEvent(new CustomEvent('td:cylindo-main-update-start', {
          detail: {
            customizer: detail,
            token: this.mainUpdateInFlightToken,
          },
        }));

        if (!this.placeholderFailed) {
          this.hideCover();
        }
        if (this.missingPreviewSignatures.has(featureSignature)) {
          this.showNoPreviewState();
        } else {
          this.updatePlaceholderPreview(detail);
          this.showLoader();
        }

        this.cylindoViewer.features = { ...this.cylindoViewer.features, ...newFeatures };
        document.dispatchEvent(new CustomEvent('td:display-cylindo'));
      }

      handleFrameChange({ detail }) {
        if (this.isFixedFrame) return;
        if (detail?.frame != null) this.activeFrame = String(detail.frame);
        if (this.cylindoViewer) {
          this.cylindoViewer.animateToFrame(Number(detail.frame));
        }
      }

      /**
       * Handle open trigger event
       * Updates Cylindo viewer features
       */
      handleOpenTrigger({ detail }) {
        if (typeof detail?.open === 'boolean') {
          viewportSelectionCache.isOpen = detail.open;
        }
        if (this.cylindoViewer) {
          this.cylindoViewer.features = {
            ...this.cylindoViewer.features,
            'VIEW': detail.open ? CYLINDO_CONFIG.STATES.OPEN : CYLINDO_CONFIG.STATES.CLOSE,
          };
        }
      }

      /**
       * Show the loader, set loading state.
       */
      showLoader() {
        if (!this.loader) {
          this.loader = this.querySelector('[data-loader]');
        }
        if (this.loader) {
          if (CYLINDO_CONFIG.SHOW_LOADING_SPINNER) {
            this.loader.classList.add('td-cylindo-loader--active');
          } else {
            this.loader.classList.remove('td-cylindo-loader--active');
          }
          this.setAttribute('aria-busy', 'true');
        }
        this.isLoading = true;
        if (!this.placeholder) {
          this.placeholder = this.querySelector('[data-cylindo-placeholder-dynamic]');
        }
      }

      /**
       * Hide the loader and placeholder, clear loading state.
       * Clears pendingPlaceholderShow so late load events do not re-show placeholder.
       * Does not hide cover when placeholder failed - keep "no preview" visible.
       */
      hideLoader() {
        this.pendingPlaceholderShow = false;
        if (!this.placeholderFailed) {
          this.hideCover();
        }
        if (!this.loader) {
          this.loader = this.querySelector('[data-loader]');
        }
        if (this.loader) {
          this.loader.classList.remove('td-cylindo-loader--active');
          this.removeAttribute('aria-busy');
        }
        this.isLoading = false;
        if (!this.placeholder) {
          this.placeholder = this.querySelector('[data-cylindo-placeholder-dynamic]');
        }
        if (this.placeholder) {
          this.placeholder.classList.remove('td-cylindo-placeholder-dynamic--active');
          this.placeholder.classList.add('td-cylindo-placeholder-dynamic--hidden');
        }
      }

      /**
       * Hide the "no preview" cover overlay (used when placeholder/3D loads successfully)
       */
      hideCover() {
        const coverMobile = sharedDOMCache.cylindoCoverMobile;
        const coverDesktop = sharedDOMCache.cylindoCoverDesktop;
        if (coverMobile) {
          coverMobile.style.opacity = '0';
          coverMobile.style.zIndex = '-1';
        }
        if (coverDesktop) {
          coverDesktop.style.opacity = '0';
          coverDesktop.style.zIndex = '-1';
        }
        sharedDOMCache.cylindoOpenTriggers.forEach((trigger) => {
          trigger.classList.remove('td--trigger-hidden');
        });
      }

      /**
       * Handle Cylindo viewer 'loaded' event
       * Uses delay to allow fabric texture to finish rendering (model may load before fabric)
       */
      handleCylindoLoaded() {
        this.applyFixedFrame();
        const completedToken = this.mainUpdateInFlightToken;
        this.completeMainUpdateIfNeeded();
        if (this.isLoading) {
          this.scheduleHideLoaderAfterMainUpdate(completedToken);
        }
      }

      /**
       * Handle Cylindo viewer 'interaction-ready' event
       * Uses delay to allow fabric texture to finish rendering
       */
      handleCylindoInteractionReady() {
        this.applyFixedFrame();
        const completedToken = this.mainUpdateInFlightToken;
        this.completeMainUpdateIfNeeded();
        if (this.isLoading) {
          this.scheduleHideLoaderAfterMainUpdate(completedToken);
        }
      }

      /**
       * Handle frame trigger image loading completion
       * Intentionally ignored for placeholder teardown.
       * Thumbnail requests can complete before the main viewer, which causes stale-frame flicker.
       * Main viewer events (loaded/interaction-ready) are the source of truth.
       */
      handleCylindoImageSet({ detail }) {
        return;
      }

      completeMainUpdateIfNeeded() {
        if (!this.mainUpdateInFlightToken) return;
        this.clearMainViewerFallbackTimeout();

        const completedToken = this.mainUpdateInFlightToken;
        document.dispatchEvent(new CustomEvent('td:cylindo-main-updated', {
          detail: {
            customizer: this.mainUpdateInFlightDetail,
            token: completedToken,
          },
        }));
        this.lastAppliedFeatureSignature = this.inFlightFeatureSignature;
        this.inFlightFeatureSignature = null;
        this.mainUpdateInFlightToken = null;
        this.mainUpdateInFlightDetail = null;
        this.processPendingCustomizerRequest();
      }

      scheduleMainViewerFallbackTimeout(updateToken) {
        this.clearMainViewerFallbackTimeout();
        this.mainViewerFallbackTimeout = setTimeout(() => {
          if (!this.isViewportActive) return;
          if (!this.mainUpdateInFlightToken || this.mainUpdateInFlightToken !== updateToken) return;

          this.completeMainUpdateIfNeeded();
          if (this.isLoading && !this.mainUpdateInFlightToken) {
            this.hideLoader();
          }
        }, CYLINDO_CONFIG.MAIN_VIEWER_FALLBACK_TIMEOUT_MS);
      }

      scheduleHideLoaderAfterMainUpdate(completedToken) {
        setTimeout(() => {
          if (!this.isViewportActive || !this.isLoading) return;
          if (this.mainUpdateInFlightToken && this.mainUpdateInFlightToken !== completedToken) return;
          if (this.mainUpdateInFlightToken) return;
          this.hideLoader();
        }, CYLINDO_CONFIG.PLACEHOLDER_HIDE_DELAY_MS);
      }

      clearMainViewerFallbackTimeout() {
        if (this.mainViewerFallbackTimeout) {
          clearTimeout(this.mainViewerFallbackTimeout);
          this.mainViewerFallbackTimeout = null;
        }
      }
    }

    customElements.define('td-cylindo-wrapper', TDCylindoWrapper);
  }

  /* ============================================
   * WEB COMPONENT: td-cylindo-frame-trigger
   * ============================================ */

  if (!customElements.get('td-cylindo-frame-trigger')) {
    class TDCylindoFrameTrigger extends HTMLElement {
      constructor() {
        super();

        this.cylindoCustomerId = this.dataset.cylindoCustomer;
        this.cylindoProduct = this.dataset.cylindoProduct;
        this.frame = this.dataset.frame;
        this.iteration = parseInt(this.dataset.iteration, 10);
        this.urlTemplate = this.dataset.urlTemplate;
        this.image = this.querySelector('img');

        this.orientation = null;
        this.currentFrame = '1';
        this.isOpen = false;
        this.currentUpdateId = 0;
        this.abortController = null;

        this.featureBuilder = new CylindoFeatureBuilder(window.spsProductType);
        this.urlBuilder = new CylindoUrlBuilder(this.urlTemplate, this.cylindoCustomerId, this.cylindoProduct);

        this._boundHandleCustomizerChange = this.handleCustomizerChange.bind(this);
        this._boundHandleClick = this.handleClick.bind(this);
        this._boundHandleOpenTrigger = this.handleOpenTrigger.bind(this);
        this._boundHandleImageError = this.handleImageError.bind(this);
        this._boundHandleImageLoad = this.handleImageLoad.bind(this);
        this._boundHandleMainViewerUpdated = this.handleMainViewerUpdated.bind(this);
        this._boundHandleViewportChange = this.handleViewportChange.bind(this);
        this.viewportMediaQuery = null;
        this.isViewportActive = false;
        this.isViewportListenerBound = false;
        this.pendingCustomizerDetail = null;
        this.pendingFallbackUpdateTimeout = null;
        this.pendingImageFallbackTimeout = null;
        this.lastMainUpdateToken = 0;
        this.hasCompletedInitialMainUpdate = false;
        this.pendingInitialGateTimeout = null;
      }

      connectedCallback() {
        if (this.cylindoCustomerId && this.frame && this.cylindoProduct) {
          this.setupViewportListener();
          this.handleViewportChange();
        }
      }

      disconnectedCallback() {
        this.teardownViewportListener();
        this.deactivateForViewport();
      }

      setupViewportListener() {
        if (!this.viewportMediaQuery) {
          this.viewportMediaQuery = window.matchMedia('(min-width: 1024px)');
        }
        if (this.isViewportListenerBound) return;

        if (typeof this.viewportMediaQuery.addEventListener === 'function') {
          this.viewportMediaQuery.addEventListener('change', this._boundHandleViewportChange);
        } else if (typeof this.viewportMediaQuery.addListener === 'function') {
          this.viewportMediaQuery.addListener(this._boundHandleViewportChange);
        }

        this.isViewportListenerBound = true;
      }

      teardownViewportListener() {
        if (!this.viewportMediaQuery || !this.isViewportListenerBound) return;

        if (typeof this.viewportMediaQuery.removeEventListener === 'function') {
          this.viewportMediaQuery.removeEventListener('change', this._boundHandleViewportChange);
        } else if (typeof this.viewportMediaQuery.removeListener === 'function') {
          this.viewportMediaQuery.removeListener(this._boundHandleViewportChange);
        }

        this.isViewportListenerBound = false;
      }

      handleViewportChange() {
        const shouldBeActive = this.shouldBeActiveForViewport();

        if (shouldBeActive && !this.isViewportActive) {
          this.activateForViewport();
          return;
        }

        if (!shouldBeActive && this.isViewportActive) {
          this.deactivateForViewport();
        }
      }

      shouldBeActiveForViewport() {
        const isDesktop = this.viewportMediaQuery ? this.viewportMediaQuery.matches : window.matchMedia('(min-width: 1024px)').matches;
        const isDesktopOnly = Boolean(this.closest('.mobile--hide'));
        const isMobileOnly = Boolean(this.closest('.desktop--hide'));

        if (isDesktopOnly) return isDesktop;
        if (isMobileOnly) return !isDesktop;
        return true;
      }

      activateForViewport() {
        this.isViewportActive = true;
        this.hasCompletedInitialMainUpdate = false;
        this.clearInitialMainUpdateFallback();
        this.bindEvents();
        thumbnailCoordinator.register(this);
        preloadManager.init(this.urlTemplate, this.cylindoCustomerId, this.cylindoProduct);
        this.hydrateFromCachedSelection();
      }

      deactivateForViewport() {
        this.isViewportActive = false;
        this.abortController?.abort();
        thumbnailCoordinator.unregister(this);
        this.clearPendingFallbackUpdate();
        this.clearPendingImageFallback();
        this.clearInitialMainUpdateFallback();
        this.unbindEvents();
      }

      unbindEvents() {
        document.removeEventListener('td:color-changed', this._boundHandleCustomizerChange);
        document.removeEventListener('td:product-selected', this._boundHandleCustomizerChange);
        document.removeEventListener('td:cylindo-main-updated', this._boundHandleMainViewerUpdated);
        document.removeEventListener('td:cylindo-open-trigger', this._boundHandleOpenTrigger);
        this.removeEventListener('click', this._boundHandleClick);

        if (this.image) {
          this.image.removeEventListener('error', this._boundHandleImageError);
          this.image.removeEventListener('load', this._boundHandleImageLoad);
        }
      }

      bindEvents() {
        this.unbindEvents();
        document.addEventListener('td:color-changed', this._boundHandleCustomizerChange);
        document.addEventListener('td:product-selected', this._boundHandleCustomizerChange);
        document.addEventListener('td:cylindo-main-updated', this._boundHandleMainViewerUpdated);
        document.addEventListener('td:cylindo-open-trigger', this._boundHandleOpenTrigger);
        this.addEventListener('click', this._boundHandleClick);

        if (this.image) {
          this.image.addEventListener('error', this._boundHandleImageError);
          this.image.addEventListener('load', this._boundHandleImageLoad);
        }
      }

      /**
       * Cache customizer detail and schedule a fallback update path.
       * Main viewer update event should arrive first and trigger thumbnail updates.
       */
      handleCustomizerChange({ detail }) {
        if (!this.isViewportActive || !detail) return;
        mergeCustomizerDetailIntoCache(detail);

        this.pendingCustomizerDetail = detail;
        if (!this.hasCompletedInitialMainUpdate) {
          this.scheduleInitialMainUpdateFallback();
          return;
        }
        this.scheduleFallbackUpdate();
      }

      handleMainViewerUpdated({ detail }) {
        if (!this.isViewportActive) return;

        const customizerDetail = detail?.customizer || this.pendingCustomizerDetail;
        if (!customizerDetail) return;
        const updateToken = detail?.token || 0;
        if (updateToken > 0 && updateToken <= this.lastMainUpdateToken) return;
        if (updateToken > 0) {
          this.lastMainUpdateToken = updateToken;
        }

        this.hasCompletedInitialMainUpdate = true;
        this.clearInitialMainUpdateFallback();
        this.pendingCustomizerDetail = customizerDetail;
        this.clearPendingFallbackUpdate();
        thumbnailCoordinator.queueUpdate(customizerDetail);
      }

      scheduleInitialMainUpdateFallback() {
        if (this.pendingInitialGateTimeout || this.hasCompletedInitialMainUpdate) return;

        this.pendingInitialGateTimeout = setTimeout(() => {
          this.pendingInitialGateTimeout = null;
          if (!this.isViewportActive || this.hasCompletedInitialMainUpdate) return;

          this.hasCompletedInitialMainUpdate = true;
          if (this.pendingCustomizerDetail) {
            thumbnailCoordinator.queueUpdate(this.pendingCustomizerDetail);
          }
        }, CYLINDO_CONFIG.PRELOAD.INITIAL_THUMBNAIL_DEFER_TIMEOUT_MS);
      }

      clearInitialMainUpdateFallback() {
        if (this.pendingInitialGateTimeout) {
          clearTimeout(this.pendingInitialGateTimeout);
          this.pendingInitialGateTimeout = null;
        }
      }

      scheduleFallbackUpdate() {
        this.clearPendingFallbackUpdate();
        this.pendingFallbackUpdateTimeout = setTimeout(() => {
          if (!this.isViewportActive || !this.pendingCustomizerDetail) return;
          thumbnailCoordinator.queueUpdate(this.pendingCustomizerDetail);
          this.pendingFallbackUpdateTimeout = null;
        }, 480);
      }

      clearPendingFallbackUpdate() {
        if (this.pendingFallbackUpdateTimeout) {
          clearTimeout(this.pendingFallbackUpdateTimeout);
          this.pendingFallbackUpdateTimeout = null;
        }
      }

      clearPendingImageFallback() {
        if (this.pendingImageFallbackTimeout) {
          clearTimeout(this.pendingImageFallbackTimeout);
          this.pendingImageFallbackTimeout = null;
        }
      }

      /**
       * Called by ThumbnailUpdateCoordinator with debounced updates
       * @param {Object} detail - The customizer event detail
       * @param {number} updateId - Unique ID for this update batch
       */
      processUpdate(detail, updateId) {
        if (!this.isViewportActive) return;
        this.currentUpdateId = updateId;
        this.orientation = detail.product?.orientation;

        const frames = getFrames(window.spsProductType, this.orientation);
        this.currentFrame = frames[this.iteration];

        const featureFragments = this.featureBuilder.buildUrlFragments(detail, { isOpen: this.isOpen });
        const newUrl = this.urlBuilder.build(this.currentFrame, featureFragments);

        this.requestRenderingUpdate(newUrl, updateId);
      }

      /**
       * Performs optimistic image update; onerror handler applies fallback URL.
       * @param {string} newUrl - The new image URL
       * @param {number} updateId - The update ID for staleness checking
       */
      requestRenderingUpdate(newUrl, updateId) {
        if (this.currentUpdateId !== updateId) return;
        this.clearPendingImageFallback();
        if (this._pendingUrl === newUrl && this.image?.src === newUrl) return;
        this._pendingUrl = newUrl;
        this._updateId = updateId;
        this.image.src = newUrl;
      }

      /**
       * Handles successful image load (from optimistic update)
       */
      handleImageLoad() {
        this.clearPendingImageFallback();
        if (this.image.src !== this._pendingUrl) return;
        this.handleImageSuccess(this.image.src);
      }

      /**
       * On image error, use base Cylindo URL without feature fragments.
       */
      handleImageError() {
        const fallbackUrl = this.urlBuilder.build(this.currentFrame, []);
        if (this._pendingUrl === fallbackUrl || this.image.src === fallbackUrl) return;

        this.clearPendingImageFallback();
        this.pendingImageFallbackTimeout = setTimeout(() => {
          if (this._pendingUrl === fallbackUrl || this.image.src === fallbackUrl) return;
          this._pendingUrl = fallbackUrl;
          this.image.src = fallbackUrl;
          this.pendingImageFallbackTimeout = null;
        }, 80);
      }

      handleImageSuccess(newUrl) {
        const frames = getFrames(window.spsProductType, this.orientation);
        const placeholderFrame = frames[0] || '1';
        if (this.currentFrame === placeholderFrame) {
          const imageUrlWithoutView = newUrl.split('feature=VIEW:')[0];
          document.dispatchEvent(new CustomEvent('td:cylindo-image-set', { detail: { image: imageUrlWithoutView } }));
        }
      }

      handleClick() {
        const frames = getFrames(window.spsProductType, this.orientation);
        const frame = frames[this.iteration];
        const scope = this.closest('.product__media__wrapper') || document;
        const hasMobileMainSteps = window.matchMedia('(max-width: 1023px)').matches
          && Boolean(scope.querySelector('[data-product-images-slider][data-cylindo-main-steps]'));
        const targetIndex = hasMobileMainSteps && Number.isInteger(this.iteration)
          ? this.iteration
          : 0;

        document.dispatchEvent(new CustomEvent('td:custom-thumbnail-change', {
          detail: {
            newIndex: targetIndex,
            viewport: window.matchMedia('(max-width: 1023px)').matches ? 'mobile' : 'desktop',
          },
        }));
        document.dispatchEvent(new CustomEvent('td:cylindo-frame-trigger', { bubbles: true, detail: { frame } }));
      }

      handleOpenTrigger({ detail }) {
        if (!this.isViewportActive) return;
        if (typeof detail?.open === 'boolean') {
          viewportSelectionCache.isOpen = detail.open;
        }
        this.isOpen = detail.open || false;

        const newUrl = this.urlBuilder.updateFeature(
          this.image.src,
          'VIEW',
          this.isOpen ? CYLINDO_CONFIG.STATES.OPEN : CYLINDO_CONFIG.STATES.CLOSE
        );

        this.requestRenderingUpdate(newUrl, this.currentUpdateId);
      }

      hydrateFromCachedSelection() {
        const cachedDetail = viewportSelectionCache.customizerDetail;
        if (cachedDetail) {
          this.handleCustomizerChange({ detail: cachedDetail });
        }

        const cachedOpen = getCachedOpenState();
        if (typeof cachedOpen === 'boolean') {
          this.handleOpenTrigger({ detail: { open: cachedOpen } });
        }
      }
    }

    customElements.define('td-cylindo-frame-trigger', TDCylindoFrameTrigger);
  }

  /* ============================================
   * WEB COMPONENT: td-cylindo-open-trigger
   * ============================================ */

  if (!customElements.get('td-cylindo-open-trigger')) {
    class TDCylindoOpenTrigger extends HTMLElement {
      constructor() {
        super();

        this.inputElement = this.querySelector('input');
        this.defaultState = this.dataset.isOpen;

        this._boundHandleChange = this.handleChange.bind(this);
        this._boundHandleOtherElementChange = this.handleOtherElementChange.bind(this);
      }

      connectedCallback() {
        if (this.inputElement) {
          this.bindEvents();

          if (this.defaultState === CYLINDO_CONFIG.STATES.OPEN) {
            this.inputElement.checked = true;
            this.handleChange();
          }
        }
      }

      disconnectedCallback() {
        if (this.inputElement) {
          this.inputElement.removeEventListener('change', this._boundHandleChange);
        }
        document.removeEventListener('td:cylindo-open-trigger', this._boundHandleOtherElementChange);
      }

      bindEvents() {
        this.inputElement.addEventListener('change', this._boundHandleChange);
        document.addEventListener('td:cylindo-open-trigger', this._boundHandleOtherElementChange);
      }

      handleChange() {
        document.dispatchEvent(
          new CustomEvent('td:cylindo-open-trigger', {
            bubbles: true,
            detail: { open: this.inputElement.checked },
          })
        );
      }

      handleOtherElementChange({ detail }) {
        this.inputElement.checked = detail.open;
      }
    }

    customElements.define('td-cylindo-open-trigger', TDCylindoOpenTrigger);
  }
})();
